import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { ProductTypes } from '@app/core/_constant/product-type.constants';
import { AuthService } from '@app/core/auth';
import { PermissionService } from '@app/core/auth/services/permission.service';
import { FactoryService } from '@app/core/factories';
import { RfqService } from '@app/core/quotes';
import { UserRoleConstants } from '@app/core/staff/store/staff.model';
import { environment } from '@app/env-service';
import { EFeatureFlag } from '@app/feature-toggle-list';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { RfqAssignBomFormComponent } from 'v2/modules/rfq/components/rfq-assign-bom-form/rfq-assign-bom-form.component';
import { DialogConfig } from 'v2/modules/shared/configs/dialog.config';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { trackByValue } from 'v2/modules/shared/util/track-by.util';
import { UiConfirmationDialogComponent } from 'v2/modules/ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';

import { EditRfqVariantsComponent } from '../edit-rfq-variants/edit-rfq-variants.component';
import { RfqImageFormPopupComponent } from '../rfq-image-form-popup/rfq-image-form-popup.component';
import { RfqImagePopupComponent } from '../rfq-image-popup/rfq-image-popup.component';
import { UpdateAlternateSkusComponent } from '../update-alternate-skus/update-alternate-skus.component';
import { UpdateMasterAlternateSkuComponent } from '../update-master-alternate-sku/update-master-alternate-sku.component';

@Component({
  selector: 'cnc-rfq-details-page',
  templateUrl: './rfq-details-page.component.html',
  styleUrls: ['./rfq-details-page.component.scss'],
})
export class RfqDetailsPageComponent implements OnInit, OnDestroy {
  url :string = environment.url;
  factories :any[] = [];
  productTypes = ProductTypes;
  private onDestroy$ = new Subject();
  constructor(
    public rfqService :RfqService,
    public authService :AuthService,
    private modalSvc :NgbModal,
    public factoryService :FactoryService,
    private cd :ChangeDetectorRef,
    public UserPermission :PermissionService,
    public sanitizer :DomSanitizer,
    private featureToggleService :FeatureToggleService,
    private dialog :MatDialog,
    private snackBar :MatSnackBar,
    private translate :TranslateService,
  ) {}

  @Input() rfq :any;
  locations :string = '';
  rfqSubscription :Subscription;
  locationsArray :any = [];

  alternateChildSKUs :any[] = [];

  showProductLinks :boolean = false;

  variants :any[] = [];
  selectedVariantTerms :any = {};

  featureFlag = EFeatureFlag;
  userRoleConstants :UserRoleConstants = UserRoleConstants;
  trackByValue = trackByValue;

  hasValidPhoto :boolean = false;
  hasValidDesignLink :boolean = false;

  productLinks :{ lv :string }[] = [];
  materials :{ mv :string }[] = [];

  @Output()
  syncBomRfqsEvent = new EventEmitter<any>();

  ngOnDestroy() {
    this.rfqSubscription.unsubscribe();
  }

  ngOnInit() {
    this.factoryService.factory$.subscribe((factories :any) => {
      this.factories = factories;
    });
    if (this.rfq && this.rfq.locations) {
      this.locations = this.rfq.locations
        .map((row) => row.location_name)
        .join(', ');
    }
    this.locationsArray = this.locations ? this.locations.split(',') : [];
    this.rfqSubscription = this.rfqService.activeRecord$.subscribe((rfq :any) => {
      if (rfq && rfq.id) {
        this.rfq = {
          ...rfq,
          tags: rfq.tags.filter((tag) => !!tag.tag_details),
        };
        if (this.rfq.preferred_vendor_id && this.factories.length > 0) {
          const factory = this.factories.find(
            (row :any) => row.id == this.rfq.preferred_vendor_id,
          );
          if (factory && factory.id) {
            this.rfq.preferred_vendor = factory;
          }
        }
        if (this.rfq && this.rfq.locations) {
          this.locations = this.rfq.locations
            .map((row) => row.location_name)
            .join(', ');
        }

        if (this.rfq.variant_parent_id > 0) {
          this.variants = JSON.parse(this.rfq.parent.product_variants).filter(
            (productVariant) => {
              return typeof productVariant !== 'string';
            },
          );

          //set condition if this.rfq.product_variants is empty or not
          if (this.variants.length > 0) {
            const childVariants = this.rfq.product_variants.filter(
              (productVariant) => {
                return typeof productVariant !== 'string';
              },
            );

            if (childVariants && childVariants.length > 0) {
              childVariants.forEach((row) => {
                this.selectedVariantTerms[row.variant_name] = row.variant_terms[0];
              });
            } else {
              this.variants.forEach((row) => {
                this.selectedVariantTerms[row.variant_name] = '';
              });
            }
          } else {
            this.variants = JSON.parse(this.rfq.parent.product_variants).filter(
              (productVariant) => {
                return typeof productVariant !== 'string';
              },
            );
          }
        }

        this.hasValidPhoto = this.#isValidLink(this.rfq?.photo);
        this.hasValidDesignLink = this.#isValidLink(this.rfq?.product_design);

        // don't show empty product links or materials
        this.productLinks =
          this.rfq.product_link?.filter((link) => link.lv.trim()) ?? [];
        this.materials =
          this.rfq.materials?.filter((material) => material.mv.trim()) ?? [];
      }
    });
    this.getChildSKU(this.rfq.id);
    this.getAlternateSKU({ id: this.rfq.id });
  }

  editVariants() {
    const modalRef = this.modalSvc.open(EditRfqVariantsComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.rfq = this.rfq;
    modalRef.result.then(
      (response) => {
        if (response?.record) {
          this.rfq = response?.record;
          this.cd.detectChanges();
        }
      },
      () => {},
    );
  }

  updateAlternateSkus() {
    const modalRef = this.modalSvc.open(UpdateAlternateSkusComponent, {});
    modalRef.componentInstance.rfq = this.rfq;
    modalRef.result.then(
      () => {
        this.cd.detectChanges();
      },
      () => {},
    );
  }

  assignBillOfMaterials() :void {
    this.dialog
      .open(RfqAssignBomFormComponent, {
        width: DialogConfig.sizes.small,
        data: {
          rfqId: this.rfq.id,
          bomRfqs: this.rfq.bom_rfqs ?? [],
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.rfq.bom_rfqs = result;
          this.syncBomRfqsEvent.emit(result);
          this.cd.detectChanges();
        }
      });
  }

  detachBillOfMaterials(index :number) :void {
    const bomRfq = this.rfq.bom_rfqs[index] ?? null;
    if (!bomRfq) {
      this.snackBar.open('No BOM to detach', 'Close', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Detach BOM',
          message:
            "Do you really want to detach '" +
            this.rfq.bom_rfqs[index]?.bom?.name +
            "'?",
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          Swal.fire('Detaching BOM from RFQ...');
          Swal.showLoading();
          this.executeDetachBillOfMaterials(this.rfq.id, bomRfq.id);
        }
      });
  }

  updateAlternateMasterSkus() {
    const modalRef = this.modalSvc.open(UpdateMasterAlternateSkuComponent, {});
    modalRef.componentInstance.rfq = this.rfq;
    modalRef.result.then(
      () => {
        this.getChildSKU(this.rfq.id);
        this.cd.detectChanges();
      },
      () => {},
    );
  }

  getChildSKU(rfqId) {
    this.rfqService.getChildSKU(rfqId).subscribe((res) => {
      this.rfq.childSku = res;
    });
  }

  private getAlternateSKU(params :any) :void {
    this.rfqService.getAlternateSKU(params).subscribe((res) => {
      this.rfq.alternate_skus = res;
    });
  }

  viewImage(id :number, rfqId :number) {
    const modalRef = this.modalSvc.open(RfqImagePopupComponent, {});
    modalRef.componentInstance.imageId = id;
    modalRef.componentInstance.skuId = rfqId;
  }

  addImage(id :number) {
    const modalRef = this.modalSvc.open(RfqImageFormPopupComponent, {});
    modalRef.componentInstance.skuId = id;
  }

  private executeDetachBillOfMaterials(rfqId :number, bomRfqId :number) :void {
    if (!navigator.onLine) {
      // If offline
      this.snackBar.open(
        'You are offline. Please check your network connection.',
        'Close',
        {
          duration: 3 * 1000, // 3 sec
        },
      );
      Swal.close();

      return; // Exit the function
    }
    this.rfqService
      .detachBOMRfq({ rfqId: rfqId, bomRfqId: bomRfqId })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (result) => {
          this.snackBar.open(result?.success, 'Success', {
            duration: 3 * 1000, // 3 sec
          });
          this.rfq.bom_rfqs = this.rfq.bom_rfqs.filter(
            (bomRfq) => bomRfq.id !== bomRfqId,
          );
          Swal.close();
          this.cd.detectChanges();
        },
        error: (error) => {
          this.snackBar.open(
            error?.error ? error.error?.error : 'Error detaching record',
            'Close',
            {
              duration: 3 * 1000, // 3 sec
            },
          );
          Swal.close();
        },
      });
  }

  #isValidLink(urls :any) {
    if (!urls) {
      return false;
    }
    if (typeof urls === 'string') {
      urls = [urls];
    }
    if (!Array.isArray(urls) || urls.length < 1) {
      return false;
    }

    // TypeScript v4.8 doesn't have URL.canParse() documented, but it for sure
    // exists and is fully supported, hence @ts-ignore. [calvin]
    // @see https://developer.mozilla.org/en-US/docs/Web/API/URL/canParse_static
    // @ts-ignore
    return URL.canParse(urls[0]); // we currently only care about the first
  }

  get featureBOMAssemblyOrdersEnabled() :boolean {
    return this.featureToggleService.featureEnabled(EFeatureFlag.ASSEMBLY_ORDER);
  }
}
